import { useIntl } from 'react-intl';
import {
  IconCloudDownload,
  IconFold,
  IconFoldDown,
  IconPlus,
  IconSparkles,
} from '@rossum/ui/icons/tabler';
import { Button, Paper, Stack, SvgIcon } from '@rossum/ui/material';
import { useIsFeatureEnabled } from '../../feature-enablement/featureEnablement';
import { FieldFinder } from './FieldFinder';

type FieldItem = {
  id: string;
  row: number;
};

type SchemaEditorToolbarProps = {
  onToggleFold: () => void;
  isFolded: boolean;
  schemaFields: FieldItem[];
  onFieldSelect: (field: FieldItem) => void;
  isJSONValid: boolean;
  onAddSectionClick: () => void;
  onCheckForChanges: () => void;
  isCheckingForChanges: boolean;
  onGenerateSchema: () => void;
};

export const SchemaEditorToolbar = ({
  onToggleFold,
  isFolded,
  schemaFields,
  onFieldSelect,
  isJSONValid,
  onAddSectionClick,
  onCheckForChanges,
  isCheckingForChanges,
  onGenerateSchema,
}: SchemaEditorToolbarProps) => {
  const intl = useIntl();
  const isFabryEnabled = useIsFeatureEnabled('mr-fabry');

  return (
    <Stack
      component={Paper}
      elevation={0}
      direction="row"
      spacing={0.5}
      alignItems="center"
      square
      sx={{
        px: 1,
        py: 0.5,
      }}
    >
      {isFabryEnabled ? (
        <Button
          onClick={onGenerateSchema}
          size="small"
          variant="text"
          startIcon={
            <SvgIcon fontSize="medium">
              <IconSparkles />
            </SvgIcon>
          }
          sx={{ textTransform: 'none', minWidth: 'auto' }}
        >
          {intl.formatMessage({ id: 'features.schemaEditor.fabry.button' })}
        </Button>
      ) : null}

      <Button
        onClick={onAddSectionClick}
        size="small"
        variant="text"
        startIcon={
          <SvgIcon fontSize="medium">
            <IconPlus />
          </SvgIcon>
        }
        data-cy="add-section-button"
        sx={{ textTransform: 'none', minWidth: 'auto' }}
      >
        {intl.formatMessage({ id: 'features.schemaEditor.addSection' })}
      </Button>

      <FieldFinder fields={schemaFields} onFieldSelect={onFieldSelect} />

      <Button
        onClick={onCheckForChanges}
        disabled={isCheckingForChanges}
        size="small"
        variant="text"
        startIcon={
          <SvgIcon fontSize="medium">
            <IconCloudDownload />
          </SvgIcon>
        }
        data-cy="check-for-changes-button"
        sx={{ textTransform: 'none', minWidth: 'auto' }}
      >
        {intl.formatMessage({ id: 'features.schemaEditor.checkForChanges' })}
      </Button>

      <Button
        onClick={onToggleFold}
        disabled={!isJSONValid}
        size="small"
        variant="text"
        startIcon={
          <SvgIcon fontSize="medium">
            {isFolded ? <IconFoldDown /> : <IconFold />}
          </SvgIcon>
        }
        data-cy="toggle-fold-button"
        sx={{ textTransform: 'none', minWidth: 'auto' }}
      >
        {intl.formatMessage({
          id: isFolded
            ? 'components.editor.tooltips.unfoldAll'
            : 'components.editor.tooltips.foldAll',
        })}
      </Button>
    </Stack>
  );
};
